import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import { Toaster } from 'sonner';
import CookieConsentBanner from "./components/CookieConsentBanner";

// Lazy load pages for better performance
const HomePage = lazy(() => import("./pages/HomePage"));
const PrivatePage = lazy(() => import("./pages/PrivatePage"));
const DevicesPage = lazy(() => import("./pages/DevicesPage"));
const SolutionsPage = lazy(() => import("./pages/SolutionsPage"));
const TargetAudiencePage = lazy(() => import("./pages/TargetAudiencePage"));
const SeniorCarePage = lazy(() => import("./pages/SeniorCarePage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-[#F0F7E6] flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#8FD16A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-[#4A6B7A]">Ielādē...</p>
    </div>
  </div>
);

// Redirect component that preserves params
const RedirectWithSlug = ({ from, to }) => {
  return <Navigate to={to} replace />;
};

function App() {
  // Hide Emergent badge
  useEffect(() => {
    const hideBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.style.display = 'none';
      }
    };
    hideBadge();
    const timer = setTimeout(hideBadge, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Set HTML lang attribute
  useEffect(() => {
    document.documentElement.setAttribute('lang', 'lv');
  }, []);

  return (
    <BrowserRouter>
      <div className="App">
        <Toaster 
          position="top-center" 
          richColors 
          toastOptions={{
            style: {
              background: '#FFFFFF',
              border: '1px solid #E6ECE8',
            },
          }}
        />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Main Pages */}
            <Route path="/" element={<HomePage />} />
            
            {/* Private Individual Journey */}
            <Route path="/privatpersonam" element={<PrivatePage />} />
            <Route path="/ierices" element={<DevicesPage />} />
            
            {/* Professional Journey */}
            <Route path="/risinajumi" element={<SolutionsPage />} />
            <Route path="/kam-paredzets" element={<TargetAudiencePage />} />
            <Route path="/senioru-aprupe" element={<SeniorCarePage />} />
            
            {/* Permanent redirects for legacy URLs (301 equivalent in SPA) */}
            <Route path="/kam-tas-paredzets" element={<Navigate to="/kam-paredzets" replace />} />
            
            {/* Blog - canonical URLs */}
            <Route path="/blogs" element={<BlogPage />} />
            <Route path="/blogs/:slug" element={<ArticlePage />} />
            
            {/* Blog legacy redirects - redirect /blog to /blogs */}
            <Route path="/blog" element={<Navigate to="/blogs" replace />} />
            <Route path="/blog/:slug" element={<BlogArticleRedirect />} />
            
            {/* Legal Pages */}
            <Route path="/privatuma-politika" element={<PrivacyPolicyPage />} />
            <Route path="/pardosanas-un-lietosanas-noteikumi" element={<TermsPage />} />
            <Route path="/juridiska-informacija" element={<LegalPage />} />
            
            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        
        {/* Cookie Consent Banner */}
        <CookieConsentBanner />
      </div>
    </BrowserRouter>
  );
}

// Component to handle /blog/:slug -> /blogs/:slug redirect
function BlogArticleRedirect() {
  const slug = window.location.pathname.split('/blog/')[1];
  return <Navigate to={`/blogs/${slug}`} replace />;
}

export default App;
